import { useState, useCallback, useRef } from 'react';
import { activate } from "../../../utils/keyboard-activate";
import { useNavigate } from 'react-router-dom';
import {
  createBatchExtraction,
  uploadPdf,
  processPdf,
} from '../../../services/batch-extractions';
import type { UploadType } from '../../../types/batch-extraction';
import { AdminPage, AdminPageHeader, AdminPanel } from '../AdminPage';
import './BatchExtractionWizard.css';

const UPLOAD_TYPES: { value: UploadType; label: string; description: string }[] = [
  {
    value: 'single_team_roster',
    label: 'Team Roster',
    description: 'PDF containing roster(s) for one or more teams',
  },
  {
    value: 'showcase',
    label: 'Showcase',
    description: 'Showcase PDF with multiple teams',
  },
];

export const BatchExtractionWizard = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Upload state
  const [name, setName] = useState('');
  const [uploadType, setUploadType] = useState<UploadType>('single_team_roster');
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setUploadError('Please select a PDF file');
        return;
      }
      setFile(selectedFile);
      setUploadError(null);

      // Auto-generate name from filename if not set
      if (!name) {
        const baseName = selectedFile.name.replace(/\.pdf$/i, '');
        setName(baseName);
      }
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      if (droppedFile.type !== 'application/pdf') {
        setUploadError('Please select a PDF file');
        return;
      }
      setFile(droppedFile);
      setUploadError(null);

      // Auto-generate name from filename if not set
      if (!name) {
        const baseName = droppedFile.name.replace(/\.pdf$/i, '');
        setName(baseName);
      }
    }
  }, [name]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (!file || !name.trim()) return;

    setUploading(true);
    setUploadError(null);
    setUploadProgress(10);

    try {
      // Step 1: Create batch and get upload URL
      const response = await createBatchExtraction({
        name: name.trim(),
        uploadType,
      });
      setUploadProgress(30);

      // Step 2: Upload PDF to signed URL
      await uploadPdf(response.uploadUrl, file);
      setUploadProgress(60);

      // Step 3: Start processing
      await processPdf(response.batchId);
      setUploadProgress(100);

      // Step 4: Navigate to review page (which handles progress display)
      navigate(`/admin/batch-extractions/${response.batchId}/review`);
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
      setUploading(false);
    }
  };

  const renderUploadStep = () => (
    <AdminPanel title="Upload PDF">
      <div className="wizard__form">
        <div className="wizard__field">
          <label htmlFor="batch-extraction-wizard-batch-name-0" className="wizard__label">Batch Name</label>
          <input id="batch-extraction-wizard-batch-name-0"
            type="text"
            className="wizard__input"
            placeholder="e.g., Summer Showcase 2024"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className="wizard__field">
          <span className="wizard__label" id="batch-extraction-wizard-roster-type-0">Roster Type</span>
          <div className="wizard__type-options" role="group" aria-labelledby="batch-extraction-wizard-roster-type-0">
            {UPLOAD_TYPES.map((type) => (
              <label
                key={type.value}
                className={`wizard__type-option ${uploadType === type.value ? 'wizard__type-option--selected' : ''}`}
              >
                <input
                  type="radio"
                  name="uploadType"
                  value={type.value}
                  checked={uploadType === type.value}
                  onChange={() => setUploadType(type.value)}
                />
                <div className="wizard__type-content">
                  <span className="wizard__type-label">{type.label}</span>
                  <span className="wizard__type-description">{type.description}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div className="wizard__field">
          <label className="wizard__label">PDF File</label>
          <div
            className={`wizard__dropzone ${file ? 'wizard__dropzone--has-file' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            {...activate(() => fileInputRef.current?.click())}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {file ? (
              <div className="wizard__file-info">
                <span className="wizard__file-icon">PDF</span>
                <span className="wizard__file-name">{file.name}</span>
                <span className="wizard__file-size">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            ) : (
              <div className="wizard__dropzone-content">
                <span className="wizard__dropzone-icon">+</span>
                <span className="wizard__dropzone-text">
                  Drop PDF here or click to select
                </span>
              </div>
            )}
          </div>
        </div>

        {uploadError && (
          <div className="admin-error" role="alert">{uploadError}</div>
        )}

        {uploading && (
          <div className="wizard__progress">
            <div className="wizard__progress-bar">
              <div
                className="wizard__progress-fill"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <span className="wizard__progress-text">
              {uploadProgress < 30 && 'Creating batch...'}
              {uploadProgress >= 30 && uploadProgress < 60 && 'Uploading PDF...'}
              {uploadProgress >= 60 && 'Starting extraction...'}
            </span>
          </div>
        )}

        <div className="wizard__actions">
          <button
            className="wizard__btn wizard__btn--secondary"
            onClick={() => navigate('/admin/batch-extractions')}
          >
            Cancel
          </button>
          <button
            className="wizard__btn wizard__btn--primary"
            onClick={handleUpload}
            disabled={!file || !name.trim() || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload & Extract'}
          </button>
        </div>
      </div>
    </AdminPanel>
  );

  return (
    <AdminPage className="wizard">
      <AdminPageHeader
        title="New Batch Extraction"
        back={
          <button
            className="wizard__back-btn"
            onClick={() => navigate('/admin/batch-extractions')}
          >
            Back
          </button>
        }
      />

      {renderUploadStep()}
    </AdminPage>
  );
};
